/**
 * NPKI 인증서 폴더/파일 목록에서 signCert.der + signPri.key 쌍을 찾는다.
 * - 파일 2개 직접 선택
 * - webkitdirectory 폴더 선택 (하위 포함)
 * - 드래그앤드롭 FileList
 */

export type NpkiCertPickResult =
  | { ok: true; der: File; key: File; pairDir: string }
  | { ok: false; message: string };

type FileWithPath = File & { webkitRelativePath?: string };

const basename = (path: string) => {
  const parts = path.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || path;
};

const dirname = (path: string) => {
  const normalized = path.replace(/\\/g, '/');
  const idx = normalized.lastIndexOf('/');
  return idx >= 0 ? normalized.slice(0, idx) : '';
};

const isDerName = (name: string) => {
  const n = name.toLowerCase();
  return n === 'signcert.der' || n.endsWith('signcert.der');
};

const isKeyName = (name: string) => {
  const n = name.toLowerCase();
  return n === 'signpri.key' || n.endsWith('signpri.key');
};

const fileDir = (file: FileWithPath) => {
  const rel = (file.webkitRelativePath || '').trim();
  if (rel) return dirname(rel);
  return '';
};

/**
 * FileList / File[] 에서 NPKI 인증서 쌍을 고른다.
 * 같은 폴더에 der+key가 있는 쌍을 우선한다.
 */
export const pickNpkiCertFiles = (files: FileList | File[] | null | undefined): NpkiCertPickResult => {
  if (!files || files.length === 0) {
    return { ok: false, message: '선택된 파일이 없습니다.' };
  }

  const list = Array.from(files) as FileWithPath[];

  // ZIP 은 브라우저에서 바로 풀지 않음 — 안내
  if (list.length === 1 && list[0].name.toLowerCase().endsWith('.zip')) {
    return {
      ok: false,
      message: 'ZIP은 먼저 압축을 해제한 뒤, 인증서 폴더를 선택해 주세요.',
    };
  }

  type Cand = { file: FileWithPath; dir: string; name: string };
  const ders: Cand[] = [];
  const keys: Cand[] = [];

  for (const file of list) {
    const name = basename(file.name);
    const dir = fileDir(file);
    if (isDerName(name)) ders.push({ file, dir, name });
    else if (isKeyName(name)) keys.push({ file, dir, name });
  }

  if (ders.length === 0 && keys.length === 0) {
    return {
      ok: false,
      message: 'signCert.der / signPri.key 파일을 찾지 못했습니다. NPKI 아래 본인 인증서 폴더를 선택해 주세요.',
    };
  }

  // 같은 디렉터리 쌍 우선
  const pairs: Array<{ der: Cand; key: Cand }> = [];
  for (const der of ders) {
    const key = keys.find((k) => k.dir === der.dir);
    if (key) pairs.push({ der, key });
  }

  if (pairs.length === 1) {
    return {
      ok: true,
      der: pairs[0].der.file,
      key: pairs[0].key.file,
      pairDir: pairs[0].der.dir || '(선택 폴더)',
    };
  }

  if (pairs.length > 1) {
    return {
      ok: false,
      message: `인증서 폴더가 ${pairs.length}개 있습니다. USER 아래 본인 인증서 폴더 하나만 선택해 주세요.`,
    };
  }

  // 디렉터리 정보가 없을 때(파일 2개만 직접 선택) — 경로가 둘 다 비어 있고 각 1개면 매칭
  if (
    ders.length === 1 &&
    keys.length === 1 &&
    !ders[0].dir &&
    !keys[0].dir
  ) {
    return {
      ok: true,
      der: ders[0].file,
      key: keys[0].file,
      pairDir: '(선택 파일)',
    };
  }

  if (ders.length === 0) {
    return { ok: false, message: 'signCert.der 파일을 찾지 못했습니다.' };
  }
  if (keys.length === 0) {
    return { ok: false, message: 'signPri.key 파일을 찾지 못했습니다.' };
  }

  return {
    ok: false,
    message: '인증서와 개인키 쌍을 특정할 수 없습니다. 본인 인증서 폴더만 선택해 주세요.',
  };
};

export const NPKI_PATH_HINT = '%USERPROFILE%\\AppData\\LocalLow\\NPKI';
